/**
 * QuePasa Inbox Management Service
 * Provides access to current inbox context and inbox utilities
 */

import { isQuePasaInbox } from '../helpers/inboxHelpers';

const inboxService = {
  _inboxesById: new Map(),

  /**
   * Get current inbox from context
   * Tries to discover from:
   * 1. URL route parameter (inbox_id)
   * 2. Vuex store (inboxes/getInbox)
   * 3. Cached inbox
   * 
   * @returns {Object|null} Current inbox or null
   * @example
   * const inbox = quepasa.service.inbox.current();
   */
  current() {
    try {
      // Try to get inbox_id from URL
      const urlMatch = window.location.pathname.match(/\/app\/accounts\/\d+\/settings\/inboxes\/(\d+)/);
      const inboxId = urlMatch ? parseInt(urlMatch[1], 10) : null;
      
      if (!inboxId) return null;

      // Try to get from Vuex store first
      const store = window.__chatwoot_store || window.$chatwootStore;
      if (store) {
        const inbox = store.getters?.['inboxes/getInbox']?.(inboxId);
        if (inbox) {
          this._inboxesById.set(inbox.id, inbox);
          return inbox;
        }
      }

      // Fallback to cached inbox
      return this._inboxesById.get(inboxId) || null;
    } catch (error) {
      console.error('[QuePasa Inbox Service] Error discovering current inbox:', error);
      return null;
    }
  },

  /**
   * Get inbox by ID
   * @param {number} inboxId - Inbox ID
   * @returns {Object|null} Inbox object or null
   * @example
   * const inbox = quepasa.service.inbox.get(803);
   */
  get(inboxId) {
    try {
      // Try to get from Vuex store first
      const store = window.__chatwoot_store || window.$chatwootStore;
      if (store) {
        const inbox = store.getters?.['inboxes/getInbox']?.(inboxId);
        if (inbox) {
          this._inboxesById.set(inbox.id, inbox);
          return inbox;
        }
      }

      // Fallback to cached inbox
      return this._inboxesById.get(inboxId) || null;
    } catch (error) {
      console.error('[QuePasa Inbox Service] Error getting inbox:', error);
      return null;
    }
  },

  /**
   * Check if inbox is QuePasa inbox
   * @param {Object} inbox - Inbox object
   * @returns {boolean} True if QuePasa inbox
   * @example
   * const isQuePasa = quepasa.service.inbox.isQuePasa(inbox);
   */
  isQuePasa(inbox) {
    return isQuePasaInbox(inbox);
  },

  /**
   * Get all QuePasa inboxes from Vuex store
   * @returns {Array} Array of inbox objects
   * @example
   * const inboxes = quepasa.service.inbox.all();
   */
  all() {
    try {
      const store = window.__chatwoot_store || window.$chatwootStore;
      if (!store) {
        return Array.from(this._inboxesById.values());
      }

      const allInboxes = store.getters?.['inboxes/getInboxes'] || [];
      const quepasaInboxes = allInboxes.filter(inbox => isQuePasaInbox(inbox));
      
      // Update cache
      quepasaInboxes.forEach(inbox => {
        this._inboxesById.set(inbox.id, inbox);
      });

      return quepasaInboxes;
    } catch (error) {
      console.error('[QuePasa Inbox Service] Error getting all inboxes:', error);
      return Array.from(this._inboxesById.values());
    }
  },

  /**
   * Clear inbox cache
   * @example
   * quepasa.service.inbox.clear();
   */
  clear() {
    this._inboxesById.clear();
    console.log('[QuePasa Inbox Service] Cache cleared');
  }
};

// Add help properties
inboxService.current.help = 'current() - Gets current inbox from context (auto-discovers from URL + Vuex store). Returns inbox object or null.';
inboxService.get.help = 'get(inboxId) - Gets inbox by ID from Vuex store or cache. Returns inbox object or null.';
inboxService.isQuePasa.help = 'isQuePasa(inbox) - Checks if inbox is QuePasa inbox. Returns boolean.';
inboxService.all.help = 'all() - Gets all QuePasa inboxes from Vuex store. Returns array of inbox objects.';
inboxService.clear.help = 'clear() - Clears inbox cache.';

export default inboxService;
